import {
    CancelOutlined,
    Info,
    PersonOutlined,
    Search,
    Share,
    WhatsApp
} from "@mui/icons-material";
import {
    Button,
    Card,
    CardActions,
    CardContent,
    CircularProgress,
    IconButton,
    InputBase
} from "@mui/material";
import { toBlob } from "html-to-image";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import AccountProgressBar from "../components/accountProgressBar";
import AddEntryForm from "../components/forms/addEntryForm";
import { getAccountsList } from "../firebase/dataHandler";
import { useTranslation } from "react-i18next";

function Accounts({ filterId, hideSearchBar, hideTodayCollectionCard }) {
  const { t } = useTranslation();
  const getUnPaidDays = (date) =>
    Math.round(
      (new Date().getTime() - new Date(date).getTime()) / (1000 * 3600 * 24)
    ) - 1;
  const getUnPaidWeeks = (date) =>
    Math.round(
      (new Date().getTime() - new Date(date).getTime()) / (1000 * 3600 * 24 * 7)
    );
  const getUnPaidMonths = (date) => {
    const today = new Date();
    const d = new Date(date);
    return (today.getFullYear() - d.getFullYear()) * 12 + today.getMonth() - d.getMonth();
  };

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [isLoading, setLoading] = useState(true);
  const [searchQuerry, setSearchQuerry] = useState(searchParams.get("q") || "");
  const [accountsList, setAccountList] = useState(null);

  useEffect(() => {
    const q = searchParams.get('q');
    if (q !== null) {
      setSearchQuerry(q);
    }
  }, [searchParams]);

  useEffect(() => {
    handleAccountList();
  }, []);

  const handleAccountList = () => {
    getAccountsList()
      .then((data) => {
        setLoading(true);
        setAccountList(data);
        setLoading(false);
      })
      .catch((err) => {
        alert(err);
      });
  };

  function isToday(timestampString) {
    // Convert the timestamp string to a Date object
    const inputDate = new Date(timestampString);
  
    // Get the current date
    const today = new Date();
  
    // Check if the input date is the same as today
    return (
      inputDate.getDate() === today.getDate() &&
      inputDate.getMonth() === today.getMonth() &&
      inputDate.getFullYear() === today.getFullYear()
    );
  }

  function getTodayCollection(accounts) {
   return  Object.values(accounts)
    .filter(a=>a.isActive || a.isActive === undefined)
    .filter(a=> a.entries)
    .map(({entries})=> Object.values(entries)).flat()
    .filter(entry=> isToday(entry.timeStamp))
    .reduce((acc, sum) => acc + Number(sum.amountPaid), 0)
  }

  function getExpectedTodayCollection(accounts) {
    return Object.values(accounts)
      .filter(a => a.isActive || a.isActive === undefined)
      .filter(a => a.type === 'daily' || a.installmentType === 'daily')
      .reduce((acc, sum) => acc + Number(sum.installmentAmount), 0)
  }

  function share({ id, text, title }) {
    toBlob(document.getElementById(id), { quality: 0.95 }).then(function (
      dataUrl
    ) {
      const file = new File([dataUrl], "picture.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        navigator
          .share({
            files: [file],
            text,
            title,
          })
          .then(() => {})
          .catch((error) => {});
      } else {
        try {
          //if location is http its change to https
          if (navigator.share === undefined) {
            if (window.location.protocol === "http:") {
              window.location.replace(
                window.location.href.replace(/^http:/, "https:")
              );
            }
          }
        } catch (error) {
          alert("Sharing failed", error);
        }
      }
    });
  }

  const sortedFilteredAccounts = useMemo(() => {
    if (!accountsList) return [];
    
    return []
      .concat(
        Object.values(accountsList)
          .filter((a) => !a.entries)
          .sort((a, b) => new Date(a.date) - new Date(b.date)),
        Object.values(accountsList).filter((a) => !!a.entries)
      )
      .sort((a, b) =>
        a.entries && b.entries
          ? new Date(
              Object.values(a.entries)
                .sort((c, d) => new Date(c.date) - new Date(d.date))
                .at(-1).date
            ) -
            new Date(
              Object.values(b.entries)
                .sort((c, d) => new Date(c.date) - new Date(d.date))
                .at(-1).date
            )
          : -1
      )
      .filter((list) => (!filterId ? true : list.clientId === filterId))
      .filter(a => a.isActive || a.isActive === undefined);
  }, [accountsList, filterId]);

  return (
    <div
      style={{
        padding: "2%",
        marginBottom: "200px",
      }}
    >

      {/* ── Header ── */}
      <div style={{ padding: '12px 4px 8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 style={{ fontSize: 26, marginBottom: 4 }}>{t('accounts.title')}</h1>
          {accountsList && (
            <small style={{ color: 'var(--text-muted)', fontSize: 11 }}>
              {Object.values(accountsList).filter(a => a.isActive || a.isActive === undefined).length} {t('accounts.active')}
            </small>
          )}
        </div>
      </div>

      <div style={{ height: 1, background: 'var(--border-subtle)', margin: '0 0 12px' }} />

      {(accountsList && !hideTodayCollectionCard) && (
        <div style={{
          margin: '0 0 12px',
          background: 'linear-gradient(135deg, rgba(21, 195, 88, 0.15) 0%, rgba(21, 195, 88, 0.05) 100%)',
          border: '1px solid var(--border-active)',
          borderRadius: 16, padding: '14px 18px',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <div>
            <p style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#0fd47a', marginBottom: 4, fontFamily: 'Inter, sans-serif' }}>{t('accounts.daily_recovery')}</p>
            <p style={{ fontSize: 12, color: 'rgba(136,146,176,0.7)', fontFamily: 'Inter, sans-serif' }}>{t('accounts.total_recovered_today')}</p>
          </div>
          <div style={{ textAlign: 'right' }}>
            <p style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-primary)', fontFamily: 'Inter, sans-serif' }}>
              ₹ {getTodayCollection(accountsList).toLocaleString('en-IN', { maximumFractionDigits: 3 })}
            </p>
            <p style={{ fontSize: 11, color: 'var(--text-secondary)', fontFamily: 'Inter, sans-serif', marginTop: 2 }}>
              / ₹ {getExpectedTodayCollection(accountsList).toLocaleString('en-IN', { maximumFractionDigits: 3 })} {t('accounts.expected')}
            </p>
          </div>
        </div>
      )}

      { !hideSearchBar && (
        <div style={{
          display: 'flex', alignItems: 'center',
          background: 'var(--bg-card)', border: '1px solid var(--border-subtle)',
          borderRadius: 999, padding: '6px 8px 6px 16px',
          margin: '0 0 14px', transition: 'border-color 0.2s',
        }}
        onFocus={(e) => e.currentTarget.style.borderColor = 'rgba(45,132,255,0.4)'}
        onBlur={(e) => e.currentTarget.style.borderColor = 'rgba(255,255,255,0.07)'}
        >
          <InputBase
            sx={{ flex: 1, fontFamily: 'Inter, sans-serif', fontSize: 13, color: 'var(--text-primary)' }}
            placeholder={t('accounts.search_placeholder')}
            value={searchQuerry}
            onChange={(e) => setSearchQuerry(e.target.value)}
          />
          <IconButton size="small" onClick={() => setSearchQuerry('')} style={{ color: !searchQuerry ? 'var(--text-muted)' : 'var(--accent-red)' }}>
            {!searchQuerry ? <Search style={{ fontSize: 18 }} /> : <CancelOutlined style={{ fontSize: 18 }} />}
          </IconButton>
        </div>
      )}
      {isLoading ? (
        <CircularProgress />
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' }}>
          {sortedFilteredAccounts
              .map((data) => {
                let paidDayCount = data.entries
                  ? Object.values(data.entries).reduce(
                      (acc, sum) => acc + (sum.isUpfrontInterest ? 0 : Number(sum.dayCount)),
                      0
                    )
                  : 0;
                let paidAmount = data.entries
                  ? Object.values(data.entries).reduce(
                      (acc, sum) => acc + (sum.isUpfrontInterest ? 0 : Number(sum.amountPaid)),
                      0
                    )
                  : 0;

                let elapsedPeriods = data.installmentType === 'daily'
                  ? getUnPaidDays(data.date)
                  : data.installmentType === 'weekly'
                  ? getUnPaidWeeks(data.date)
                  : data.installmentType === 'monthly' ? getUnPaidMonths(data.date) : 0;

                let remainingInstallments = data.installmentPeriod - paidDayCount;
                let missedPayments = Math.min(remainingInstallments, Math.max(0, elapsedPeriods - paidDayCount));
                let futureInstallments = remainingInstallments - missedPayments;

                let isOverdue = elapsedPeriods > data.installmentPeriod && remainingInstallments > 0;
                let overduePeriods = isOverdue ? elapsedPeriods - data.installmentPeriod : 0;
                let periodType = data.installmentType === 'daily' ? 'days' : data.type === 'weekly' ? 'weeks' : 'months';
                let isNotTallied = paidAmount !== (paidDayCount * data.installmentAmount);
                const query = searchQuerry ? searchQuerry.toLowerCase() : '';
                const matchName = (data?.name || '').toLowerCase().includes(query);
                const matchId = (data?.accountId || '').toLowerCase().includes(query);
                const show = !query || matchName || matchId;

                return show && (
                    <div
                      key={data.accountId}
                      className="glass-card"
                      style={{
                        marginBottom: 16,
                        borderRadius: 16,
                        overflow: 'hidden',
                        padding: 0,
                        border: 'none',
                        boxShadow: 'var(--shadow-md)',
                      }}
                    >
                      <Card
                        sx={{
                          padding: '16px 18px',
                          border: '1px solid var(--border-subtle)',
                          background: 'var(--bg-card)',
                        }}
                      >
                        <CardContent id={data.accountId} style={{ padding: 0 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                            {/* Last entry hint */}
                            {data.entries ? (
                              <p style={{ fontSize: 10, color: 'var(--text-muted)', fontFamily: 'Inter, sans-serif', margin: 0 }}>
                                {t('accounts.last_entry')}: {new Date(Object.values(data.entries).at(-1).date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: '2-digit' })}
                              </p>
                            ) : <div />}
                            
                            <div style={{ display: 'flex', gap: 6 }}>
                              {isNotTallied && (
                                <div style={{ fontSize: 9, background: 'rgba(248, 113, 113, 0.1)', color: '#f87171', padding: '2px 6px', borderRadius: 4, fontWeight: 600, border: '1px solid rgba(248, 113, 113, 0.2)' }}>
                                  {t('accounts.not_tallied')}
                                </div>
                              )}
                              <div style={{ fontSize: 9, background: 'rgba(45, 132, 255, 0.1)', color: '#2d84ff', padding: '2px 6px', borderRadius: 4, fontWeight: 600, border: '1px solid rgba(45, 132, 255, 0.2)', textTransform: 'uppercase' }}>
                                {data.installmentType || 'daily'}
                              </div>
                            </div>
                          </div>

                        {/* Main row */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          {/* Left: name + details */}
                          <div style={{ flex: 1 }}>
                            <div style={{ display: 'flex', justifyContent: 'flex-start', marginLeft: '-6px' }}>
                              <Button
                                color="inherit"
                                size="medium"
                                onClick={() => navigate(`/customers/${data.clientId.split('_')[0]}`)}
                                variant="text"
                                startIcon={<PersonOutlined style={{ fontSize: 16 }} />}
                                style={{ 
                                  textTransform: 'none', fontFamily: 'Inter, sans-serif', 
                                  fontWeight: 600, fontSize: 16, padding: '4px 8px', 
                                  color: 'var(--text-primary)', zIndex: 10, minWidth: 0, 
                                  textAlign: 'left' 
                                }}
                              >
                                {data.name}
                              </Button>
                            </div>
                            <p style={{ textAlign: 'left', fontSize: 20, fontWeight: 700, color: 'var(--accent-blue)', letterSpacing: '-0.02em', fontFamily: 'Inter, sans-serif', marginBottom: 4 }}>
                              ₹ {data.amount.toLocaleString('en-IN', { maximumFractionDigits: 3 })}
                            </p>
                            <p style={{ textAlign: 'left', fontSize: 12, color: 'var(--text-secondary)', fontFamily: 'Inter, sans-serif' }}>
                              ₹ {data.installmentAmount.toLocaleString('en-IN', { maximumFractionDigits: 3 })} {t('accounts.each')}
                            </p>
                          </div>

                          {/* Right: paid + balance */}
                          <div style={{ textAlign: 'right', minWidth: 90 }}>
                            <p style={{ fontSize: 16, fontWeight: 700, color: 'var(--accent-green)', fontFamily: 'Inter, sans-serif', marginBottom: 2 }}>
                              ₹ {paidAmount.toLocaleString('en-IN', { maximumFractionDigits: 3 })}
                            </p>
                            <p style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 8, fontFamily: 'Inter, sans-serif' }}>{t('accounts.recovered')}</p>
                            <p style={{ fontSize: 14, fontWeight: 600, color: 'var(--accent-red)', fontFamily: 'Inter, sans-serif', marginBottom: 2 }}>
                              ₹ {(data.amount - paidAmount).toLocaleString('en-IN', { maximumFractionDigits: 3 })}
                            </p>
                            <p style={{ fontSize: 10, color: 'var(--text-muted)', fontFamily: 'Inter, sans-serif' }}>
                              {futureInstallments} {t('accounts.pending')}
                              {missedPayments > 0 && <span style={{ color: 'var(--accent-red)', marginLeft: 4 }}> + {missedPayments} {t('accounts.in_arrears')}</span>}
                            </p>
                            {isOverdue ? (
                              <div style={{ marginTop: 6, display: 'inline-block', background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', border: '1px solid rgba(239, 68, 68, 0.2)', padding: '4px 8px', borderRadius: 6, fontSize: 10, fontWeight: 700, letterSpacing: '0.02em' }}>
                                {t('accounts.overdue_by')} {overduePeriods} {periodType.toUpperCase()}
                              </div>
                            ) : remainingInstallments > 0 ? (
                              <div style={{ marginTop: 6, display: 'inline-block', background: 'rgba(45, 132, 255, 0.1)', color: '#2d84ff', border: '1px solid rgba(45, 132, 255, 0.2)', padding: '4px 8px', borderRadius: 6, fontSize: 10, fontWeight: 700, letterSpacing: '0.02em' }}>
                                {t('accounts.due_in')} {data.installmentPeriod - elapsedPeriods} {periodType.toUpperCase()}
                              </div>
                            ) : (
                              <div style={{ marginTop: 6, display: 'inline-block', background: 'rgba(10, 158, 92, 0.1)', color: '#0A9E5C', border: '1px solid rgba(10, 158, 92, 0.2)', padding: '4px 8px', borderRadius: 6, fontSize: 10, fontWeight: 700, letterSpacing: '0.02em' }}>
                                {t('accounts.completed')}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Progress bar */}
                        <div style={{ marginTop: 14 }}>
                          <AccountProgressBar
                            key={data.accountId + '0'}
                            keyId={`progress_${data.accountId}`}
                            startDate={data.date}
                            period={data.installmentPeriod}
                            installmentType={data.installmentType || 'daily'}
                            values={[
                            paidDayCount,
                              data.installmentType === 'daily'
                                ? getUnPaidDays(data.date)
                                : data.installmentType === 'weekly'
                                ? getUnPaidWeeks(data.date)
                                : data.installmentType === 'monthly' ? getUnPaidMonths(data.date) : 0,
                            ]}
                          />
                        </div>
                      </CardContent>

                      {/* Actions */}
                      <CardActions style={{ padding: '12px 0 0', margin: 0, gap: 4 }}>
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/account/${data.accountId.split('_')[0]}_${data.clientId.split('_')[0]}`)}
                          style={{ background: 'rgba(45,132,255,0.1)', color: '#2d84ff', borderRadius: 10, padding: 8 }}
                        >
                          <Info style={{ fontSize: 16 }} />
                        </IconButton>
                        <div style={{ flex: 1 }}>
                          <AddEntryForm
                            accountId={data.accountId}
                            refresh={handleAccountList}
                            installmentAmount={data.installmentAmount}
                            installmentType={data.installmentType || 'daily'}
                            buttonStyle={{ width: '100%' }}
                          />
                        </div>
                        <IconButton
                          size="small"
                          sx={{ color: '#25D366', background: 'rgba(37,211,102,0.1)', borderRadius: 10, padding: 1 }}
                          onClick={() => {
                            let type = data.installmentType === 'daily' ? 'days' : data.installmentType === 'weekly' ? 'weeks' : data.installmentType === 'monthly' ? 'months' : '';
                            let lastEntry = data.entries ? Object.values(data.entries).sort((a, b) => new Date(a.date) - new Date(b.date)).at(-1) : { date: 'NA', amountPaid: 0, dayCount: 0 };
                            let text = `${t('accounts.share_ac_no')} ${data.accountId.split('_')[0]}\n${t('accounts.share_name')} ${data.name}\n${t('accounts.share_issued')} ${new Date(data.date).toLocaleDateString('en-IN')}\n${t('accounts.share_last_entry')} ${new Date(lastEntry.date).toLocaleDateString('en-IN')} - ₹ ${lastEntry.amountPaid}\n${t('accounts.share_paid')} ${paidDayCount} installments / ₹ ${paidAmount}\n${t('accounts.share_balance')} ${data.installmentPeriod - paidDayCount} ${type} / ₹ ${data.amount - paidAmount}`;
                            if ((navigator.platform.includes('arm') || navigator.platform.toLowerCase().includes('android')) && data.contact) {
                              share({ id: data.accountId, text, title: `${data.name} | Account Details` });
                            } else if (data.contact) {
                              window.open(`https://wa.me/+91${data.contact}?text=${encodeURIComponent(text)}`);
                            }
                          }}
                        >
                          {!data.contact ? <Share style={{ fontSize: 18 }} /> : <WhatsApp style={{ fontSize: 18 }} />}
                        </IconButton>
                      </CardActions>
                      </Card>
                    </div>
                  );
              })}
        </div>
      )}
      {/* <AddButton/> */}
    </div>
  );
}

export default Accounts;
